import { initializeApp, getApp, getApps } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  Persistence,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase (guarded for dev hot reloads)
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

const isBrowserEnvironment = typeof window !== 'undefined' && typeof document !== 'undefined';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

const encode = (value: string) => encodeURIComponent(value);
const decode = (value: string) => decodeURIComponent(value);

const writeCookie = (key: string, value: string, maxAgeSeconds = COOKIE_MAX_AGE_SECONDS) => {
  if (!isBrowserEnvironment) return;
  const secureFlag = typeof window !== 'undefined' && window.isSecureContext ? '; Secure' : '';
  document.cookie = `${encode(key)}=${encode(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Strict${secureFlag}`;
};

const readCookie = (key: string): string | null => {
  if (!isBrowserEnvironment) return null;
  const target = encode(key);
  const entries = document.cookie?.split(';') ?? [];
  for (const entry of entries) {
    const [rawKey, ...rest] = entry.trim().split('=');
    if (!rawKey) continue;
    if (rawKey === target) {
      return decode(rest.join('='));
    }
  }
  return null;
};

const removeCookie = (key: string) => writeCookie(key, '', 0);

type ExtendedPersistence = Persistence & {
  _isAvailable(): Promise<boolean>;
  _set(key: string, value: string): Promise<void>;
  _get(key: string): Promise<string | null>;
  _remove(key: string): Promise<void>;
};

const cookiePersistence: ExtendedPersistence = {
  type: 'LOCAL',
  async _isAvailable(): Promise<boolean> {
    // Navigator.cookieEnabled can be unreliable on localhost; rely on DOM availability instead.
    return isBrowserEnvironment;
  },
  async _set(key: string, value: string): Promise<void> {
    writeCookie(key, value);
  },
  async _get(key: string): Promise<string | null> {
    return readCookie(key);
  },
  async _remove(key: string): Promise<void> {
    removeCookie(key);
  },
};

const persistencePriority = [cookiePersistence, browserLocalPersistence, browserSessionPersistence];

let authInitializedWithCustomPersistence = false;
let authInstance;

try {
  authInstance = initializeAuth(app, { persistence: persistencePriority });
  authInitializedWithCustomPersistence = true;
} catch {
  authInstance = getAuth(app);
}

// Get Firebase services
export const auth = authInstance;
export const db = getFirestore(app);
export const functions = getFunctions(app);

// Ensure auth state survives browser restarts by prioritizing secure cookies.
const ensurePersistence = async () => {
  if (authInitializedWithCustomPersistence || !isBrowserEnvironment) {
    return;
  }

  for (const persistence of persistencePriority) {
    try {
      await setPersistence(auth, persistence);
      return;
    } catch (error) {
      console.warn('Failed to set auth persistence, trying next fallback', error);
    }
  }

  console.error('Unable to configure Firebase auth persistence; auth will be ephemeral.');
};

export const authPersistenceReady = ensurePersistence();

// Initialize Analytics
export const analytics = typeof window !== 'undefined' ? getAnalytics(app) : null; 